import { deleteCellSelection } from "@tiptap/pm/tables";

import { selectionTouchesLockedBlock } from "@/components/tiptap/lock/utils";
import { moveToMath, nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { isCellSelection } from "@/components/tiptap/utils/isCellSelection";
import { deleteSelectedNodes } from "@/input/utils/editOperators";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { EditorView } from "@tiptap/pm/view";

export function deleteSelection(doc: DocumentModel, view: EditorView): boolean {
  const { selection, tr } = view.state;

  let { from, to } = selection;
  if (from === to) {
    return false;
  }

  if (!doc.editor.tiptap) {
    return false;
  }

  // The MobX delete below runs before the PM transaction, so filterTransaction alone can't guard it.
  if (selectionTouchesLockedBlock(view.state)) {
    return true;
  }

  if (isCellSelection(selection)) {
    return deleteCellSelection(view.state, view.dispatch);
  }

  const toNode = view.state.doc.resolve(to).nodeBefore;

  if (toNode?.type.name === "codeBlock" && from === to - toNode.nodeSize) {
    const { schema } = view.state;
    view.dispatch(view.state.tr.replaceWith(from, to, schema.nodes.paragraph.create()));
    doc.editor.tiptap?.commands.focus(from + 1);
    return true;
  }

  let fromPartial = false;
  const fromNode = view.state.doc.resolve(from).nodeAfter;
  if (nodeIsExpression(fromNode) && !doc.selection.isWrapperNodeSelected(fromNode.attrs.nodeId)) {
    if (doc.selection.isWrapperNodeSelected(fromNode.attrs.nodeId, true)) {
      fromPartial = true;
    }
    from += 1;
  }

  let toPartial = false;
  if (nodeIsExpression(toNode) && !doc.selection.isWrapperNodeSelected(toNode.attrs.nodeId)) {
    if (doc.selection.isWrapperNodeSelected(toNode.attrs.nodeId, true)) {
      toPartial = true;
    }
    to -= 1;
  }

  // Check if the `to` position is adjacent to an expression that will become
  // a merge target after deletion (even if `to` itself is not inside an expression).
  const toAdjacentNode = !toPartial ? view.state.doc.resolve(to).nodeAfter : null;
  const toAdjacentIsExpression = nodeIsExpression(toAdjacentNode);

  deleteSelectedNodes(doc);

  // When a partial expression will merge with another expression after deletion,
  // ensure cursor is at the start of the right expression's inline node so
  // the merge code's cursorIsBetween check works correctly for both
  // forward and backward selections.
  if (fromPartial) {
    let rightNodeId: string | null = null;
    if (toPartial && toNode) {
      rightNodeId = toNode.attrs.nodeId as string;
    } else if (toAdjacentIsExpression && toAdjacentNode) {
      rightNodeId = toAdjacentNode.attrs.nodeId;
    }

    if (rightNodeId && fromNode && fromNode.attrs.nodeId !== rightNodeId) {
      const rightWrapper = doc.nodes.get(rightNodeId);
      if (rightWrapper?.is(WrapperNodeModel)) {
        const rightInline = rightWrapper.inlineNode;
        if (rightInline) {
          doc.cursor.moveTo(rightInline, 0);
        }
      }
    }
  }

  if (from < to) {
    tr.delete(from, to);
    view.dispatch(tr);

    if (!fromPartial && !toPartial) {
      // After dispatch, fix the cursor position. During deleteSelectedNodes,
      // setTextCursor may setNodeSelection which corrupts the selection.
      // The mapped `from` position gives the correct cursor location after deletion.
      const mappedPos = tr.mapping.map(from, -1);
      const pos = Math.min(mappedPos, view.state.doc.content.size);
      doc.editor.tiptap?.chain().setTextSelection({ from: pos, to: pos }).focus().run();
    }

    // After deleting mixed content that partially overlaps an expression,
    // place the math cursor inside the surviving expression so onSelectionUpdate
    // doesn't auto-select all remaining nodes via selectWrapperNode.
    // cursor.moveTo calls resetSelection() which clears inlineNodeRanges.
    if (fromPartial || toPartial) {
      const cursorPos = view.state.selection.from;
      const nodeAtCursor = view.state.doc.nodeAt(cursorPos);
      if (nodeIsExpression(nodeAtCursor)) {
        doc.editor.tiptap.commands.setNodeSelection(cursorPos);
        moveToMath(doc, nodeAtCursor, { placeCursorToEnd: fromPartial });
      }
    }
  }

  return true;
}
